import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query'
import type { AxiosError } from 'axios'
import axios from 'axios'

import { eventNewZoomParticipants } from '@/queries/urls'
import { DRScriptData, Event, ZoomParticipant } from '@/types'

type ScriptDataMutationResponse = DRScriptData
type ScriptDataErrorResponse = { detail: string }
type ScriptDataMutationContext = {
  previousState?: DRScriptData
} & Record<string, unknown>
type ScriptDataQueryKey = ['script_data', string]
type ZoomParticipantsQueryKey = ['zoom_participants', string]

export const useSaveScriptDataMutation = (
  event?: Event,
  options?: UseMutationOptions<
    ScriptDataMutationResponse,
    AxiosError<ScriptDataErrorResponse>,
    DRScriptData,
    ScriptDataMutationContext
  >,
) => {
  const queryClient = useQueryClient()
  const queryKey: ScriptDataQueryKey = ['script_data', event?.id ?? 'unknown']
  const {
    onMutate: optionsOnMutate,
    onError: optionsOnError,
    onSettled: optionsOnSettled,
    ...restOptions
  } = options ?? {}

  return useMutation<
    ScriptDataMutationResponse,
    AxiosError<ScriptDataErrorResponse>,
    DRScriptData,
    ScriptDataMutationContext
  >({
    ...restOptions,
    mutationFn: async (payload: DRScriptData) => {
      if (!event) {
        throw new Error('Cannot save script data without an event')
      }
      return axios
        .post<DRScriptData>(event.script_data_url, payload)
        .then(r => r.data)
    },
    onMutate: async (payload: DRScriptData) => {
      await queryClient.cancelQueries(queryKey)

      const previousState = queryClient.getQueryData<DRScriptData>(queryKey)
      queryClient.setQueryData(queryKey, payload)

      const userContext = await optionsOnMutate?.(payload)

      return {
        ...(userContext ?? {}),
        previousState,
      }
    },
    onError: (error, variables, context) => {
      queryClient.setQueryData(queryKey, context?.previousState)
      optionsOnError?.(error, variables, context)
    },
    onSettled: (data, error, variables, context) => {
      optionsOnSettled?.(data, error, variables, context)
    },
  })
}

export const useSubmitScriptDataMutation = (
  event?: Event,
  options?: Omit<
    UseMutationOptions<
      ScriptDataMutationResponse,
      AxiosError<ScriptDataErrorResponse>,
      DRScriptData,
      unknown
    >,
    'mutationFn'
  >,
) =>
  useMutation<
    ScriptDataMutationResponse,
    AxiosError<ScriptDataErrorResponse>,
    DRScriptData
  >({
    mutationFn: async (payload: DRScriptData) => {
      if (!event) {
        throw new Error('Cannot submit script data without an event')
      }
      return axios
        .post<DRScriptData>(event.submit_script_data_url, payload)
        .then(r => r.data)
    },
    ...options,
  })

export const useGetScriptDataQuery = (
  event?: Event,
  options?: Omit<
    UseQueryOptions<
      DRScriptData,
      AxiosError<ScriptDataErrorResponse>,
      DRScriptData,
      ScriptDataQueryKey
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  const queryKey: ScriptDataQueryKey = ['script_data', event?.id ?? 'unknown']
  const isEnabled = (options?.enabled ?? true) && !!event
  const { data, isLoading, isError, error, refetch } = useQuery<
    DRScriptData,
    AxiosError<ScriptDataErrorResponse>,
    DRScriptData,
    ScriptDataQueryKey
  >({
    queryKey,
    cacheTime: Infinity,
    queryFn: async () => {
      if (!event) {
        throw new Error('Cannot fetch script data without an event')
      }
      return axios
        .get<DRScriptData>(event.script_data_url)
        .then(res => res.data)
    },
    ...options,
    enabled: isEnabled,
  })

  return { data, isLoading, isError, error, refetch }
}

export const useEventZoomParticipants = (
  event?: Event,
  options?: Omit<
    UseQueryOptions<
      ZoomParticipant[],
      AxiosError,
      ZoomParticipant[],
      ZoomParticipantsQueryKey
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  const queryKey: ZoomParticipantsQueryKey = [
    'zoom_participants',
    event?.id ?? 'unknown',
  ]
  const isEnabled = (options?.enabled ?? true) && !!event

  const { data, isLoading, isError, error, refetch } = useQuery<
    ZoomParticipant[],
    AxiosError,
    ZoomParticipant[],
    ZoomParticipantsQueryKey
  >({
    queryKey,
    cacheTime: Infinity,
    queryFn: async () => {
      if (!event) {
        return []
      }
      const url = eventNewZoomParticipants(event.id)
      return axios.get<ZoomParticipant[]>(url).then(res => res.data)
    },
    placeholderData: [],
    enabled: isEnabled,
    ...options,
  })

  return { data, isLoading, isError, error, refetch }
}
